import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Scissors,
  Plus,
  Search,
  Loader2,
  Calendar,
  DollarSign,
  Clock,
  X,
  Shield,
  ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import NewBadge from '../components/NewBadge';

const CosplaysPage = () => {
  const [cosplays, setCosplays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStartChooser, setShowStartChooser] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [newCosplay, setNewCosplay] = useState({
    title: '',
    character_name: '',
    series_name: '',
    description: '',
    status: 'planning',
    target_event: '',
    target_date: '',
    budget: ''
  });

  const fetchCosplays = useCallback(async () => {
    try {
      const response = await api.get('/cosplays');
      setCosplays(response.data);
    } catch (error) {
      toast.error('Failed to load cosplays');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCosplays();
  }, [fetchCosplays]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const payload = {
        ...newCosplay,
        budget: newCosplay.budget ? parseFloat(newCosplay.budget) : 0,
        target_date: newCosplay.target_date || null
      };
      
      await api.post('/cosplays', payload);
      toast.success('Cosplay created!');
      setShowCreateModal(false);
      setNewCosplay({
        title: '',
        character_name: '',
        series_name: '',
        description: '',
        status: 'planning',
        target_event: '',
        target_date: '',
        budget: ''
      });
      fetchCosplays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create cosplay');
    } finally {
      setCreating(false);
    }
  };

  const filteredCosplays = cosplays.filter(cosplay => {
    const matchesSearch = 
      cosplay.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cosplay.character_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cosplay.series_name.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || cosplay.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed': return 'status-completed';
      case 'in_progress': return 'status-in_progress';
      default: return 'status-planning';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'in_progress': return 'In Progress';
      default: return 'Planning';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="cosplays-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold">My Cosplays</h1>
          <p className="mt-1">{cosplays.length} total cosplays</p>
        </div>
        <Button
          onClick={() => setShowStartChooser(true)}
          className="win-btn-default gap-2 font-semibold"
          data-testid="create-cosplay-btn"
        >
          <Plus className="w-5 h-5" />
          New Cosplay
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" />
          <Input
            placeholder="Search cosplays..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 h-12"
            data-testid="search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48 h-12" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="planning">Planning</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Cosplays Grid */}
      {filteredCosplays.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Scissors className="w-12 h-12 mx-auto mb-4" />
          <h3 className="text-lg font-heading font-semibold mb-2">
            {searchQuery || statusFilter !== 'all' ? 'No Cosplays Found' : 'No Cosplays Yet'}
          </h3>
          <p className="mb-6">
            {searchQuery || statusFilter !== 'all'
              ? 'Try adjusting your search or filter'
              : 'Start your first cosplay project!'}
          </p>
          {!searchQuery && statusFilter === 'all' && (
            <Button
              onClick={() => setShowCreateModal(true)}
              className="win-btn-default gap-2 font-semibold"
            >
              <Plus className="w-5 h-5" />
              Create Cosplay
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCosplays.map((cosplay) => (
            <Link
              key={cosplay.id}
              to={`/cosplays/${cosplay.id}`}
              className="glass-card overflow-hidden group"
              data-testid={`cosplay-card-${cosplay.id}`}
            >
              {/* Cover Image */}
              <div className="aspect-video bg-slate-800/50 relative overflow-hidden">
                {cosplay.cover_image ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL || ''}${cosplay.cover_image}`}
                    alt={cosplay.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Scissors className="w-12 h-12" />
                  </div>
                )}
                <div className="absolute top-3 right-3">
                  <span className={`px-3 py-1 text-xs font-medium ${getStatusClass(cosplay.status)}`}>
                    {getStatusLabel(cosplay.status)}
                  </span>
                </div>
              </div>

              {/* Info */}
              <div className="p-6">
                <h3 className="font-heading font-semibold text-lg truncate">
                  {cosplay.title}
                </h3>
                <p className="text-sm truncate mt-1">
                  {cosplay.character_name}
                  {cosplay.series_name && ` • ${cosplay.series_name}`}
                </p>

                {/* Progress */}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span>Progress</span>
                    <span className="font-mono">{cosplay.progress}%</span>
                  </div>
                  <Progress value={cosplay.progress} className="h-2" />
                </div>

                {/* Stats */}
                <div className="flex items-center gap-4 mt-4 text-sm">
                  <div className="flex items-center gap-1">
                    <DollarSign className="w-4 h-4" />
                    <span className="font-mono">${cosplay.total_spent.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span className="font-mono">{cosplay.total_time_hours}h</span>
                  </div>
                  {cosplay.target_date && (
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(cosplay.target_date).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Start chooser — regular cosplay vs build a Mandalorian in MandoMaster */}
      <Dialog open={showStartChooser} onOpenChange={setShowStartChooser}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading">Start a new project</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <button
              onClick={() => { setShowStartChooser(false); setShowCreateModal(true); }}
              data-testid="start-cosplay-btn"
              className="w-full text-left p-4 flex items-center gap-4 group"
            >
              <div className="w-11 h-11 win-well flex items-center justify-center flex-shrink-0">
                <Scissors className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold">Create a cosplay</p>
                <p className="text-sm">Track any cosplay project — tasks, budget, photos and more.</p>
              </div>
              <ArrowRight className="w-5 h-5 flex-shrink-0" />
            </button>

            <button
              onClick={() => { setShowStartChooser(false); window.open('https://mando.kybersuite.com', '_blank', 'noopener'); }}
              data-testid="start-mandomaster-btn"
              className="w-full text-left p-4 flex items-center gap-4 group"
            >
              <div className="w-11 h-11 win-well flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold flex items-center gap-2">Build a Mandalorian <NewBadge since="2026-06-20" /></p>
                <p className="text-sm">Design custom armor in MandoMaster — your build comes back here as a cosplay.</p>
              </div>
              <ArrowRight className="w-5 h-5 flex-shrink-0" />
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading">Create New Cosplay</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreate} className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={newCosplay.title}
                onChange={(e) => setNewCosplay({...newCosplay, title: e.target.value})}
                placeholder="e.g., Sephiroth Build 2026"
                required
                className="win-input"
                data-testid="cosplay-title-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Character Name *</Label>
                <Input
                  value={newCosplay.character_name}
                  onChange={(e) => setNewCosplay({...newCosplay, character_name: e.target.value})}
                  placeholder="Sephiroth"
                  required
                  className="win-input"
                  data-testid="cosplay-character-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Series/Source</Label>
                <Input
                  value={newCosplay.series_name}
                  onChange={(e) => setNewCosplay({...newCosplay, series_name: e.target.value})}
                  placeholder="Final Fantasy VII"
                  className="win-input"
                  data-testid="cosplay-series-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={newCosplay.description}
                onChange={(e) => setNewCosplay({...newCosplay, description: e.target.value})}
                placeholder="Notes about your cosplay plans..."
                className="win-input min-h-[80px]"
                data-testid="cosplay-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={newCosplay.status}
                  onValueChange={(value) => setNewCosplay({...newCosplay, status: value})}
                >
                  <SelectTrigger className="win-input" data-testid="cosplay-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planning">Planning</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Budget ($)</Label>
                <Input
                  type="number"
                  value={newCosplay.budget}
                  onChange={(e) => setNewCosplay({...newCosplay, budget: e.target.value})}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="win-input font-mono"
                  data-testid="cosplay-budget-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Target Event</Label>
                <Input
                  value={newCosplay.target_event}
                  onChange={(e) => setNewCosplay({...newCosplay, target_event: e.target.value})}
                  placeholder="Comic Con 2026"
                  className="win-input"
                  data-testid="cosplay-event-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Target Date</Label>
                <Input
                  type="date"
                  value={newCosplay.target_date}
                  onChange={(e) => setNewCosplay({...newCosplay, target_date: e.target.value})}
                  className="win-input"
                  data-testid="cosplay-date-input"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowCreateModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={creating}
                className="flex-1 win-btn-default font-semibold"
                data-testid="submit-cosplay-btn"
              >
                {creating ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Create Cosplay'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CosplaysPage;
